import { useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { HelpCircle, Users, Briefcase, Monitor } from 'lucide-react'

const inputCls = "w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors text-sm"

const Tooltip = ({ text }) => (
  <div className="relative inline-flex group ml-1.5 align-middle">
    <HelpCircle size={13} className="text-gray-600 hover:text-violet-400 cursor-help transition-colors" />
    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-xs text-gray-300 leading-relaxed opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-700" />
    </div>
  </div>
)

const TOOLTIP_CREATIVO = "Queste informazioni ci permettono di tenerti nel nostro database creativo. Se in futuro avessimo bisogno di figure professionali per un progetto, potremmo contattarti direttamente. Tutto è completamente opzionale."

const Login = () => {
  const { signIn } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const from = location.state?.from || '/'

  const [isRegister, setIsRegister] = useState(false)
  const [forgotMode, setForgotMode] = useState(false)

  // Tipo account
  const [tipoAccount, setTipoAccount] = useState('member') // 'member' | 'client'

  // Campi base
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [nickname, setNickname] = useState('')

  // Campi creativi (opzionali, solo per member)
  const [ruoloCr, setRuoloCr] = useState('')
  const [bio, setBio] = useState('')
  const [emailContatto, setEmailContatto] = useState('')
  const [linkedin, setLinkedin] = useState('')
  const [website, setWebsite] = useState('')
  const [portfolio, setPortfolio] = useState('')
  const [disponibile, setDisponibile] = useState(true)

  // Campi cliente (opzionali, solo per client)
  const [azienda, setAzienda] = useState('')

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)

  const handleForgot = async (e) => {
    e.preventDefault()
    setError(null); setMessage(null)
    if (!email.trim()) { setError('Inserisci la tua email.'); return }
    setLoading(true)
    const { supabase } = await import('../../supabaseClient')
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (resetErr) setError(resetErr.message)
    else setMessage('Se l\'email è registrata, riceverai un link per reimpostare la password. Controlla anche lo spam.')
    setLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    setLoading(true)

    if (isRegister) {
      const { supabase } = await import('../../supabaseClient')

      // Costruiamo i metadata da passare al trigger handle_new_user
      const metadata = {
        full_name: fullName,
        nickname,
        role: tipoAccount === 'client' ? 'client' : 'member',
      }

      if (tipoAccount === 'member') {
        if (ruoloCr) metadata.ruolo_creativo = ruoloCr
        if (bio) metadata.bio = bio
        if (emailContatto) metadata.email_contatto = emailContatto
        if (linkedin) metadata.linkedin_url = linkedin
        if (website) metadata.website_url = website
        if (portfolio) metadata.portfolio_url = portfolio
        metadata.disponibile = String(disponibile)
      }

      if (tipoAccount === 'client' && azienda) {
        metadata.azienda = azienda
      }

      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      })

      if (signUpError) {
        setError(signUpError.message)
        setLoading(false)
        return
      }

      setMessage('Controlla la tua email per confermare la registrazione.')
    } else {
      const { error: signInError } = await signIn(email, password)
      if (signInError) setError(signInError.message)
      else navigate(from, { replace: true })
    }

    setLoading(false)
  }

  const switchMode = () => {
    setIsRegister(v => !v)
    setError(null)
    setMessage(null)
    setTipoAccount('member')
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <img
            src="/PLUTONIC_LOGO_WHITE_NOBG.png"
            alt="Plutonic Studio"
            className="h-12 w-auto object-contain mx-auto mb-3"
          />
          <p className="text-gray-500 text-sm">Studio Management</p>
        </div>

        {forgotMode ? (
          <form onSubmit={handleForgot} className="space-y-4">
            <div>
              <h2 className="text-white font-semibold mb-1">Recupera password</h2>
              <p className="text-gray-500 text-sm">Inserisci la tua email: ti invieremo un link per impostare una nuova password.</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="nome@esempio.com" className={inputCls} />
            </div>
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            {message && <p className="text-green-400 text-sm text-center">{message}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors">
              {loading ? '...' : 'Invia link di recupero'}
            </button>
            <p className="text-center text-sm text-gray-500">
              <button type="button" onClick={() => { setForgotMode(false); setError(null); setMessage(null) }}
                className="text-violet-400 hover:text-violet-300 transition-colors">
                ← Torna al login
              </button>
            </p>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ── Selezione tipo account (solo in registrazione) ── */}
          {isRegister && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Sei un…</p>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setTipoAccount('member')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    tipoAccount === 'member'
                      ? 'border-violet-500 bg-violet-500/10 text-white'
                      : 'border-gray-800 bg-gray-900 text-gray-400 hover:border-gray-600'
                  }`}>
                  <Users size={22} />
                  <span className="text-sm font-medium">Creativo</span>
                  <span className="text-xs text-center leading-tight opacity-70">Parte del team o freelance</span>
                </button>
                <button type="button" onClick={() => setTipoAccount('client')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    tipoAccount === 'client'
                      ? 'border-violet-500 bg-violet-500/10 text-white'
                      : 'border-gray-800 bg-gray-900 text-gray-400 hover:border-gray-600'
                  }`}>
                  <Briefcase size={22} />
                  <span className="text-sm font-medium">Cliente</span>
                  <span className="text-xs text-center leading-tight opacity-70">Revisione e feedback</span>
                </button>
              </div>
            </div>
          )}

          {/* ── Campi base ── */}
          {isRegister && (
            <>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nome completo</label>
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)}
                  required placeholder="Mario Rossi" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nickname</label>
                <input type="text" value={nickname} onChange={e => setNickname(e.target.value)}
                  required placeholder="mario_vfx" className={inputCls} />
              </div>

              {/* Campo azienda solo per clienti */}
              {tipoAccount === 'client' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Azienda / Brand <span className="text-gray-600">(opzionale)</span>
                  </label>
                  <input value={azienda} onChange={e => setAzienda(e.target.value)}
                    placeholder="es. Acme S.r.l." className={inputCls} />
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              required placeholder="nome@esempio.com" className={inputCls} />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              required placeholder="••••••••" className={inputCls} />
            {!isRegister && (
              <button type="button" onClick={() => { setForgotMode(true); setError(null); setMessage(null) }}
                className="text-xs text-violet-400 hover:text-violet-300 transition-colors mt-2">
                Password dimenticata?
              </button>
            )}
          </div>

          {/* ── Profilo creativo opzionale (solo member) ── */}
          {isRegister && tipoAccount === 'member' && (
            <div className="pt-2 space-y-4">
              <div className="flex items-center gap-2 pb-1 border-b border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Profilo creativo</p>
                <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">opzionale</span>
                <Tooltip text={TOOLTIP_CREATIVO} />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Ruolo creativo</label>
                <input value={ruoloCr} onChange={e => setRuoloCr(e.target.value)}
                  placeholder="es. Motion Designer, Editor, Colorist..."
                  className={inputCls} />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Bio</label>
                <textarea value={bio} onChange={e => setBio(e.target.value)}
                  rows={2} placeholder="Raccontati brevemente..."
                  className="w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors resize-none" />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Email di contatto</label>
                <input type="email" value={emailContatto} onChange={e => setEmailContatto(e.target.value)}
                  placeholder="email@esempio.com" className={inputCls} />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">LinkedIn</label>
                <input value={linkedin} onChange={e => setLinkedin(e.target.value)}
                  placeholder="https://linkedin.com/in/..." className={inputCls} />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Sito web</label>
                <input value={website} onChange={e => setWebsite(e.target.value)}
                  placeholder="https://..." className={inputCls} />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Portfolio</label>
                <input value={portfolio} onChange={e => setPortfolio(e.target.value)}
                  placeholder="https://..." className={inputCls} />
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div onClick={() => setDisponibile(v => !v)}
                    className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${disponibile ? 'bg-violet-600' : 'bg-gray-700'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${disponibile ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                  <span className="text-sm text-gray-300">Disponibile per progetti</span>
                </label>
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          {message && <p className="text-green-400 text-sm text-center">{message}</p>}

          <button type="submit" disabled={loading}
            className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors mt-2">
            {loading ? '...' : isRegister ? 'Registrati' : 'Accedi'}
          </button>
        </form>
        )}

        {!forgotMode && (
        <p className="text-center text-sm text-gray-500 mt-6">
          {isRegister ? 'Hai già un account?' : 'Non hai un account?'}{' '}
          <button onClick={switchMode} className="text-violet-400 hover:text-violet-300 transition-colors">
            {isRegister ? 'Accedi' : 'Registrati'}
          </button>
        </p>
        )}

        {!forgotMode && (
          <div className="text-center mt-5">
            <Link to="/download" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-violet-300 transition-colors">
              <Monitor size={13} /> Scarica l'app per PC
            </Link>
          </div>
        )}

        <p className="text-center text-xs text-gray-500 mt-4">
          Utilizzando questa piattaforma accetti la nostra{' '}
          <Link to="/privacy" className="text-violet-400 hover:text-violet-300 underline underline-offset-2 transition-colors">
            Privacy Policy
          </Link>
        </p>

      </div>
    </div>
  )
}

export default Login
